import type { TrayCard, Week } from '../curriculum/_lib/auto-place'
import { withMockElapsed } from '../curriculum/_lib/mock-week'

export const MOCK_STAGING_TRAY: TrayCard[] = [
  {
    id: 'c100',
    title: 'Binary Search refresh',
    type: 'coding',
    meta: '⚠ decay · last seen 12 wks · ~1h',
    est: 1,
    decay: true,
  },
  {
    id: 'c101',
    title: 'Grokking Behavioral #5',
    type: 'behavioral',
    meta: '🎬 Video · Conflict deep-dive · ~0.5h',
    est: 0.5,
  },
]

// Written without `elapsed`: these weeks have labels rather than an
// engagement's real dates, so `withMockElapsed` derives it from the badge
// below. Stating it here per week is what lets a fixture drift into a `past`
// week that has not elapsed.
const MOCK_WEEK_FIXTURES: Omit<Week, 'elapsed' | 'clientActivity'>[] = [
  {
    id: 'w12',
    label: 'Week 12 · Feb 16–22',
    status: 'past',
    // The mock board folds by age, not by emptiness — w12 has two done cards.
    // Both producers get to answer "does this deserve folding" their own way,
    // which is why the answer is its own field rather than a status member.
    folded: true,
    open: false,
    days: [
      {
        label: 'MON 16',
        kind: 'normal',
        budget: 3,
        cards: [
          {
            id: 'c102',
            title: 'Two Sum II',
            type: 'coding',
            meta: 'Two pointers · ~0.5h · ✓',
            est: 0.5,
            done: true,
          },
        ],
      },
      { label: 'TUE 17', kind: 'normal', budget: 3, cards: [] },
      {
        label: 'WED 18',
        kind: 'normal',
        budget: 3,
        cards: [
          {
            id: 'c103',
            title: '3Sum',
            type: 'coding',
            meta: 'Two pointers · ~1.5h · ✓',
            est: 1.5,
            done: true,
          },
        ],
      },
      { label: 'THU 19', kind: 'normal', budget: 3, cards: [] },
      { label: 'FRI 20', kind: 'normal', budget: 3, cards: [] },
      { label: 'SAT 21', kind: 'normal', budget: 2, cards: [] },
      { label: 'SUN 22', kind: 'normal', budget: 2, cards: [] },
    ],
  },
  {
    id: 'w13',
    label: 'Week 13 · Feb 23–Mar 1',
    status: 'past',
    folded: false,
    open: false,
    days: [
      {
        label: 'MON 23',
        kind: 'normal',
        budget: 3,
        cards: [
          {
            id: 'c104',
            title: 'Longest Increasing Subsequence',
            type: 'coding',
            meta: 'DP · ~1.5h · ✓',
            est: 1.5,
            done: true,
          },
        ],
      },
      { label: 'TUE 24', kind: 'normal', budget: 3, cards: [] },
      {
        label: 'WED 25',
        kind: 'normal',
        budget: 3,
        cards: [
          {
            id: 'c105',
            title: 'Coin Change (attempt)',
            type: 'coding',
            meta: 'DP · ~1.5h · ✓',
            est: 1.5,
            done: true,
          },
        ],
      },
      { label: 'THU 26', kind: 'normal', budget: 3, cards: [] },
      {
        label: 'FRI 27',
        kind: 'normal',
        budget: 3,
        cards: [
          {
            id: 'c106',
            title: 'AI mock — DP set',
            type: 'interview',
            meta: '🤖 AI mock · ~1h · ✓',
            est: 1,
            done: true,
          },
        ],
      },
      { label: 'SAT 28', kind: 'normal', budget: 2, cards: [] },
      { label: 'SUN 1', kind: 'normal', budget: 2, cards: [] },
    ],
  },
  {
    id: 'w14',
    label: 'Week 14 · Mar 2–8',
    status: 'published',
    folded: false,
    open: true,
    days: [
      {
        label: 'MON 2',
        kind: 'normal',
        budget: 3,
        cards: [
          {
            id: 'c107',
            title: 'Coin Change',
            type: 'coding',
            meta: 'DSA · DP · ~1.5h · ✓',
            est: 1.5,
            done: true,
          },
        ],
      },
      {
        label: 'TUE 3',
        kind: 'normal',
        budget: 3,
        cards: [
          {
            id: 'c108',
            title: 'URL Shortener',
            type: 'sd',
            meta: 'Sys design · ~2h · ✓',
            est: 2,
            done: true,
          },
        ],
      },
      {
        label: 'WED 4',
        kind: 'normal',
        budget: 3,
        cards: [
          {
            id: 'c109',
            title: 'Min Stack',
            type: 'coding',
            meta: 'DSA · Stacks · ~1h',
            est: 1,
          },
          {
            id: 'c110',
            title: 'Accountability coach · 5:00pm',
            type: 'other',
            meta: '📅 calendar · 30 min',
            est: 0,
            fixed: true,
          },
        ],
      },
      {
        label: 'THU 5',
        kind: 'blocked',
        note: 'Blocked — client travel',
        cards: [],
      },
      {
        label: 'FRI 6',
        kind: 'interview',
        note: 'Google phone screen · 11:00 · confirmed',
        cards: [],
      },
      {
        label: 'SAT 7',
        kind: 'normal',
        budget: 2,
        cards: [
          {
            id: 'c111',
            title: 'Hard Feedback story',
            type: 'behavioral',
            meta: 'Behavioral · STAR · ~1h',
            est: 1,
          },
        ],
      },
      {
        label: 'SUN 8',
        kind: 'normal',
        budget: 2,
        cards: [
          {
            id: 'c112',
            title: 'LRU Cache',
            type: 'coding',
            meta: 'Linked lists · ~1.5h',
            est: 1.5,
            repeat: 'seen 9 wks ago',
          },
        ],
      },
    ],
  },
  {
    id: 'w15',
    label: 'Week 15 · Mar 9–15',
    status: 'draft',
    folded: false,
    open: true,
    days: [
      {
        label: 'MON 9',
        kind: 'normal',
        budget: 3,
        cards: [
          {
            id: 'c113',
            title: 'Rate Limiter',
            type: 'sd',
            meta: 'Key tech · ~2h',
            est: 2,
            repeat: 'attempted 6 wks ago',
          },
        ],
      },
      {
        label: 'TUE 10',
        kind: 'normal',
        budget: 3,
        cards: [
          {
            id: 'c114',
            title: 'EPI 10.1–10.3',
            type: 'coding',
            meta: '📖 Heaps ch. 10 · ~1.5h',
            est: 1.5,
          },
        ],
      },
      { label: 'WED 11', kind: 'normal', budget: 3, cards: [] },
      {
        label: 'THU 12',
        kind: 'avail',
        note: 'Meta recruiter may book 1–4pm — keep light',
        cards: [],
      },
      {
        label: 'FRI 13',
        kind: 'normal',
        budget: 3,
        cards: [
          {
            id: 'c115',
            title: 'Grokking Behavioral #4',
            type: 'behavioral',
            meta: '🎬 Video · ~0.5h',
            est: 0.5,
          },
        ],
      },
      {
        label: 'SAT 14',
        kind: 'normal',
        budget: 2,
        cards: [
          {
            id: 'c116',
            title: 'Peer mock — booked · 2:00pm',
            type: 'interview',
            meta: '📅 calendar · 60 min',
            est: 0,
            fixed: true,
          },
        ],
      },
      { label: 'SUN 15', kind: 'normal', budget: 2, cards: [] },
    ],
  },
  {
    id: 'w16',
    label: 'Week 16 · Mar 16–22',
    status: 'draft',
    folded: false,
    open: false,
    days: [
      {
        label: 'MON 16',
        kind: 'normal',
        budget: 3,
        cards: [
          {
            id: 'c117',
            title: 'NeetCode SD module 3',
            type: 'sd',
            meta: '🧩 Module · ~2h',
            est: 2,
          },
        ],
      },
      { label: 'TUE 17', kind: 'normal', budget: 3, cards: [] },
      { label: 'WED 18', kind: 'normal', budget: 3, cards: [] },
      { label: 'THU 19', kind: 'normal', budget: 3, cards: [] },
      {
        label: 'FRI 20',
        kind: 'avail',
        note: 'Google onsite window opens',
        cards: [],
      },
      { label: 'SAT 21', kind: 'normal', budget: 2, cards: [] },
      { label: 'SUN 22', kind: 'normal', budget: 2, cards: [] },
    ],
  },
]

export const MOCK_SCHEDULE_WEEKS: Week[] =
  MOCK_WEEK_FIXTURES.map(withMockElapsed)
